// import instance from "../core/provider";

import instance from "../core/instance";

export interface SignInPayload {
  email: string;
  password: string;
}

export interface SignInResponse {
  user: {
    id: number;
    email: string;
    fullname: string;
    role: string;
    active?: boolean;
  };
  token: string;
  refreshToken: string;
}

export interface SignInMigrationResponse {
  requiresMigration: true;
  message: string;
  email: string;
  phoneNumber: string;
}

export interface CredentialDocumentItem {
  id?: string;
  type?: string;
  description?: string;
  preview?: string;
  url?: string;
}

export interface DoctorRegisterPayload {
  fullname: string;
  email: string;
  password: string;
  phoneNumber: string;
  gender: "male" | "female";
  institution: string;
  licenseId: string;
  dob?: string;
  speciality?: string;
  profile_image?: string;
  /** Optional; usually applied after upload via PATCH /doctors/:id */
  credentialDocuments?: CredentialDocumentItem[];
  /** api-medpharma SignupDoctorDto.registrationPlatform */
  registrationPlatform?: string;
}

export interface CreateDoctorPrescriptionMedicationPayload {
  medicine: string;
  dose?: string;
  route?: string;
  dosage?: string;
  period?: string;
  quantity?: number;
}

export interface CreateDoctorPrescriptionPayload {
  patientName: string;
  patientEmail?: string;
  patientContact: string;
  image?: string;
  notes?: string;
  patientLocation?: string;
  institution?: string;
  doctorContact?: string;
  /** Optional — doctor can type in the patient's insurance details. */
  insuranceProvider?: string;
  policyNumber?: string;
  /** Structured line items — saved as real OrderDetail rows on the backend. */
  medications?: CreateDoctorPrescriptionMedicationPayload[];
  intakeMode?: "catalog_request" | "upload";
  /** Where this request came from — see RequestSource on the backend.
   *  Always "doctor-website" for requests raised from this app. */
  source?: "doctor-website";
}

/**
 * Body for POST /appointments/report/:id — mirrors api-medpharma's
 * CreateReportDto. `prescription` (an uploaded image URL) and
 * `prescriptionMedications` (the E-prescription draft) are mutually
 * exclusive — a report only ever has one or the other, matching
 * new-doctor-app's Upload/E-prescription toggle.
 */
export interface CreateReportPayload {
  recommendations: string;
  remarks?: string;
  diagnosis?: string;
  prescription?: string;
  prescriptionMedications?: CreateDoctorPrescriptionMedicationPayload[];
}

/**
 * Body for POST /appointments/report/:id/eprescription — turns a report's
 * drafted `prescriptionMedications` into a real DoctorPrescription. Sent
 * explicitly (not silently re-read from the draft) so the doctor can fix a
 * dose/quantity at confirm time. Mirrors api-medpharma's
 * CreateReportEPrescriptionDto exactly — same 4 optional fields the
 * standalone e-prescription screen (CreateDoctorPrescriptionPayload) offers.
 */
export interface CreateReportEPrescriptionPayload {
  medications: CreateDoctorPrescriptionMedicationPayload[];
  patientEmail?: string;
  patientLocation?: string;
  insuranceProvider?: string;
  policyNumber?: string;
  /** Where this request came from — see RequestSource on the backend.
   *  Always "doctor-website" for requests raised from this app. */
  source?: "doctor-website";
}

/**
 * GET/POST /appointments/report/:id response shape — mirrors
 * api-medpharma's DoctorReport entity. `doctorPrescription` is only ever
 * set once the doctor has actually confirmed "Generate e-prescription";
 * until then it's `null`/undefined even if `prescriptionMedications` has
 * a full draft sitting on it.
 */
export interface DoctorReportItem {
  id?: number;
  recommendations: string;
  remarks?: string;
  diagnosis?: string;
  prescription?: string;
  prescriptionMedications?: CreateDoctorPrescriptionMedicationPayload[];
  doctorPrescription?: DoctorPrescriptionItem | null;
}

export interface DrugItem {
  id: number;
  name: string;
  unitprice?: number;
  requiresprescription?: boolean;
}

export interface DoctorPrescriptionOrderDetailItem {
  id: number;
  drugName: string;
  unitPrice: number;
  quantity: number;
  route?: string;
  dosage?: string;
  duration?: string;
  frequency?: string;
  instructions?: string;
  notes?: string;
}

export interface DoctorPrescriptionItem {
  id: number;
  orderId: number;
  patientName: string;
  patientEmail?: string;
  patientContact: string;
  image?: string;
  notes?: string;
  location?: string;
  institution?: string;
  contact?: string;
  name?: string;
  email?: string;
  /**
   * Patient insurance, typed in by the doctor at creation time. Backend
   * response field is `insurance` (matches the sibling Prescription
   * entity's column) even though the create payload field is
   * `insuranceProvider` — see CreateDoctorPrescriptionPayload above.
   */
  insurance?: string;
  policyNumber?: string;
  status: string;
  paymentStatus?: string;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
  pdfUrl?: string;
  /** Structured line items, saved as real OrderDetail rows. */
  doctorOrderDetails?: DoctorPrescriptionOrderDetailItem[];
}

export type DiagnosticPaymentMethod = "cash" | "insurance" | "partner_company";

export type DiagnosticRequestType = "lab" | "xray";

export interface CreateDiagnosticPayload {
  fullname?: string;
  email?: string;
  contact?: string;
  paymentmethod: DiagnosticPaymentMethod;
  provider?: string;
  idType?: string;
  idNumber?: string;
  notes?: string;
  image?: string;
  schedule_date: string;
  schedule_time: string;
  /** Legacy single test. Prefer `tests`. */
  diagnosticPriceId?: number;
  /** Multiple tests per order — each a catalogue pick or a free-typed custom test. */
  tests?: Array<{ diagnosticPriceId?: number; customTest?: string }>;
  labId: number;
  /** Lab test vs X-Ray — see DiagnosticRequestType on the backend. */
  requestType?: DiagnosticRequestType;
  /** Where this request came from — see RequestSource on the backend.
   *  Always "doctor-website" for requests raised from this app. */
  source?: "doctor-website";
}

export interface DiagnosticItem {
  id: number;
  orderId: number;
  fullname?: string;
  email?: string;
  contact?: string;
  status: string;
  paymentStatus?: string;
  schedule_date: string;
  schedule_time: string;
  notes?: string;
  totalAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LabItem {
  id: number;
  name: string;
  description: string;
  center: string;
}

export interface DiagnosticPriceItem {
  id: number;
  name: string;
  cost: number;
  laboratory: string;
  technician?: string;
}

const DataProvider = {
  signIn: async (payload: SignInPayload): Promise<{ data: SignInResponse }> => {
    return await instance.post("/auth/signin", payload);
  },
  doctorRegister: async (
    payload: DoctorRegisterPayload,
  ): Promise<{ data: SignInResponse }> => {
    return await instance.post("/auth/doctor/register", payload);
  },
  refreshToken: async (refreshToken: string) => {
    return await instance.post("/auth/refresh-token", { refreshToken });
  },
  verifyOtpMigration: async (payload: {
    email?: string;
    phoneNumber?: string;
    otpCode: string;
  }) => {
    return await instance.post("/auth/verify-otp-migration", payload);
  },
  setNewPassword: async (payload: {
    email?: string;
    phoneNumber?: string;
    newPassword: string;
  }) => {
    return await instance.post("/auth/set-new-password", payload);
  },
  forgotpassword: async (payload: { email: string }) => {
    return await instance.post("/user-forgot-password", payload);
  },
  /** Verify OTP for password reset. Per API: GET /user-forgot-password/verify?email=...&otpCode=... (email-only). */
  verifypasswordotp: async (email: string, otpCode: string) => {
    const params = new URLSearchParams({
      email: email.trim().toLowerCase(),
      otpCode: otpCode.trim(),
    });
    return await instance.get(
      `/user-forgot-password/verify?${params.toString()}`,
    );
  },
  resetPassword: async (payload: {
    email: string;
    token: string;
    newPassword: string;
  }) => {
    return await instance.post("/user-forgot-password/update", payload);
  },
  getData: async (page: number, size: number) => {
    return await instance.get(
      `/prescriptions/query?pageNumber=${page}&pageSize=${size}`,
    );
  },
  getOrder: async (id: string) => {
    return await instance.get(`/prescriptions/${id}`);
  },
  getPartners: async (filters: string | null = null) => {
    return await instance.get(`/partners?${filters}`);
  },
  cancelOrder: async (data: any) => {
    return await instance.patch(`/prescriptions/${data.id}/cancel`, {
      reason: data.reason,
    });
  },
  addInsurance: async (data: any) => {
    return await instance.post("/me/insurance", data);
  },
  getInsurance: async () => {
    return await instance.get("/me/insurance");
  },
  deleteInsurance: async (id: string) => {
    return await instance.delete(`/me/insurance/${id}`);
  },
  getDelivery: async () => {
    return await instance.get("/me/address");
  },
  deleteDelivery: async (id: string) => {
    return await instance.delete(`/me/address/${id}`);
  },
  addAddress: async (data: any) => {
    return await instance.post("/me/address", data);
  },
  requestS3: async (data: any) => {
    return await instance.post("/upload", data);
  },
  cloudUpload: async (data: any) => {
    return await instance.post("/upload/img", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  submitPrescription: async (data: any) => {
    return await instance.post("/prescriptions", data);
  },
  deliveryMethods: async () => {
    return await instance.get("/delivery-methods");
  },
  createDoctorPrescription: async (data: CreateDoctorPrescriptionPayload) => {
    const res = await instance.post("/doctors/prescriptions", data);
    return res;
  },
  getDoctorPrescriptionsQuery: async (
    pageNumber: number = 1,
    pageSize: number = 20,
    params?: { customerName?: string; orderId?: string; status?: string },
  ) => {
    const search = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      ...(params?.customerName && { customerName: params.customerName }),
      ...(params?.orderId && { orderId: params.orderId }),
      ...(params?.status && { status: params.status }),
    }).toString();
    return await instance.get(`/doctors/prescriptions/query?${search}`);
  },
  getDoctorPrescriptionById: async (id: number) => {
    return await instance.get(`/doctors/prescriptions/${id}`);
  },
  searchDrugs: async (query: string) => {
    if (!query || query.trim().length < 2) return { data: [] as DrugItem[] };
    const filter = `name||$contL||${encodeURIComponent(query.trim())}`;
    const res = await instance.get(`/drugs?filter=${filter}&limit=25`);
    return res;
  },
  /** @deprecated Use getDoctorPrescriptionsQuery for doctor app */
  doctorPrescription: async (data: any) => {
    return await instance.post("/doctors/prescriptions", data);
  },
  prescription: async () => {
    return await instance.get("/prescriptions");
  },
  getProfile: async () => {
    return await instance.get("/auth/me");
  },
  getDoctorProfile: async () => {
    return await instance.get("/doctors/me");
  },
  updateProfile: async (data: any) => {
    return await instance.patch("/auth/me", data);
  },
  updateMyDoctorProfile: async (data: {
    institution?: string;
    licenseId?: string;
    speciality?: string;
    bio?: string;
    calendarInvitesEnabled?: boolean;
  }) => {
    return await instance.patch("/auth/me/doctor", data);
  },
  registerToken: async (payload: any) => {
    return await instance.post("/notifications", payload);
  },
  getNotifications: async (filter = false) => {
    return await instance.get("/notifications/messages?readAll=" + filter);
  },
  markNotificationAsRead: async (id: string | number) => {
    return await instance.patch(`/notifications/messages/${id}/read`);
  },
  submitConsultation: async (payload: any) => {
    return await instance.post("/consultations", payload);
  },
  getConsultations: async () => {
    return await instance.get("/consultations");
  },
  getConsultationPrices: async () => {
    return await instance.get("/consultations-prices");
  },
  getStaffData: async () => {
    return await instance.get("/partner-staff");
  },
  addStaffData: async (payload: any) => {
    return await instance.post("/partner-staff", payload);
  },
  updateStaffData: async (id: string, payload: any) => {
    return await instance.patch(`/partner-staff/${id}`, payload);
  },
  verifystaffData: async (email: string) => {
    return await instance.patch(`/partner-staff/verify/${email}`);
  },
  sendVerificationEmail: async (payload: any) => {
    return await instance.post("/verify", payload);
  },
  verifyStaffEmail: async (id: string) => {
    return await instance.get(`/verify/${id}`);
  },
  submitDiagnostic: async (payload: CreateDiagnosticPayload) => {
    return await instance.post("/diagnostics", payload);
  },
  getDiagnosticsQuery: async (
    pageNumber: number = 1,
    pageSize: number = 20,
    params?: {
      customerName?: string;
      status?: string;
      orderId?: string;
      requestType?: DiagnosticRequestType;
    },
  ) => {
    const search = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      ...(params?.customerName && { customerName: params.customerName }),
      ...(params?.status && { status: params.status }),
      ...(params?.orderId && { orderId: params.orderId }),
      ...(params?.requestType && { requestType: params.requestType }),
    }).toString();
    return await instance.get<{
      diagnostics: DiagnosticItem[];
      totalPages: number;
    }>(`/diagnostics/query?${search}`);
  },
  getDiagnosticById: async (id: number) => {
    return await instance.get<DiagnosticItem>(`/diagnostics/${id}`);
  },
  getDiagnosticPrices: async () => {
    return await instance.get<
      DiagnosticPriceItem[] | { data: DiagnosticPriceItem[] }
    >("/diagnostic-prices");
  },
  getLabsQuery: async (pageNumber: number = 1, pageSize: number = 50) => {
    return await instance.get<{ labs: LabItem[]; totalPages: number }>(
      `/labs/query?pageNumber=${pageNumber}&pageSize=${pageSize}`,
    );
  },

  getMedicalRecords: async (page: number, size: number) => {
    return await instance.get(
      `/medical-records/query?pageNumber=${page}&pageSize=${size}`,
    );
  },
  getDoctors: async (page: number, size: number) => {
    return await instance.get(
      `/doctors/query?pageNumber=${page}&pageSize=${size}`,
    );
  },
  getDoctorSchedules: async (doctorId: number) => {
    return await instance.get(`/schedule/${doctorId}`);
  },
  getScheduleForDate(date: string) {
    return instance.get(`/schedule/day/query?date=${encodeURIComponent(date)}`);
  },
  createAppointment: async (payload: any) => {
    return await instance.post("/appointments", payload);
  },
  getUpcomingAppointment: async () => {
    return await instance.get("/appointments");
  },
  getAllAppointment: async (page: number, size: number, customerName?: string) => {
    const params = new URLSearchParams({
      pageNumber: String(page),
      pageSize: String(size),
      ...(customerName ? { customerName } : {}),
    });
    return await instance.get(`/appointments/query?${params.toString()}`);
  },
  getSingleAppointment: async (apptId: string) => {
    return await instance.get(`/appointments/${apptId}`);
  },

  /** Generate consultation transcription from meeting (Doctor only). Returns updated appointment with meetingTranscript. */
  generateTranscription: async (appointmentId: string) => {
    return await instance.post(
      `/appointments/${appointmentId}/meeting/transcription/generate`,
    );
  },

  // Reports are immutable once created — matches new-doctor-app exactly
  // (the backend rejects PATCH on this route unconditionally as a second
  // line of defense), so there's no updateReport here anymore. A report's
  // e-prescription draft can still be edited/generated after the fact —
  // see generateEPrescriptionFromReport below — but the report's own
  // recommendations/remarks/diagnosis/upload can't be changed post-create.
  createReport: async (appointmentId: string, payload: CreateReportPayload) => {
    return await instance.post<DoctorReportItem>(
      `/appointments/report/${appointmentId}`,
      payload,
    );
  },
  getReport: async (appointmentId: string) => {
    return await instance.get<DoctorReportItem>(`/appointments/report/${appointmentId}`);
  },
  // Turns a report's drafted prescriptionMedications into a real,
  // official DoctorPrescription — see CreateReportEPrescriptionPayload's
  // own doc comment. A report can only ever generate one; calling this
  // again once doctorPrescription is already set is a backend no-op/error,
  // same as new-doctor-app's own confirm step.
  generateEPrescriptionFromReport: async (
    appointmentId: string,
    payload: CreateReportEPrescriptionPayload,
  ) => {
    return await instance.post<DoctorPrescriptionItem>(
      `/appointments/report/${appointmentId}/eprescription`,
      payload,
    );
  },

  getUserReports: async (userId: string) => {
    return await instance.get(`/appointments/reports/${userId}`);
  },

  getLabCeneters: async (page?: number, size?: number) => {
    return await instance.get(
      `/labs/query?pageNumber=${page}&pageSize=${size}`,
    );
  },

  payDiagnosticOrder: async (id: number, payload: any) => {
    return await instance.post(`/payments/diagnostic/${id}`, payload);
  },

  sendOTp: async (otp: number, reference: any) => {
    return await instance.get(`/payments/send-otp/${otp}/${reference}`);
  },

  //Doctor Services
  getDoctorServices: async (doctorId?: number) => {
    return await instance.get(`/doctors/service/${doctorId}`);
  },

  onboardDoctor: async (payload: any) => {
    return await instance.post("/doctors", payload);
  },
  updateDoctorProfile: async (doctorId: number, data: any) => {
    return await instance.patch(`/doctors/${doctorId}`, data);
  },

  getHistory: async (doctorId: number, page: number, size: number) => {
    return await instance.get(
      `/history/query?doctorId=${doctorId}&pageNumber=${page}&pageSize=${size}`,
    );
  },
  getPayouts: async (doctorId: number) => {
    return await instance.get(`/withdrawals/${doctorId}`);
  },
  // Backend registers this as PATCH (schedule.controller.ts
  // `toggleDisableTimeframe`), not GET — was previously wired to `.get(...)`,
  // which would 404/405 against the real endpoint. Matches new-doctor-app's
  // `scheduleApi.toggleDisable`.
  disableTimeframe: async (scheduleId: number, timeFrameId: number) => {
    return await instance.patch(`/schedule/disable/${scheduleId}/${timeFrameId}`);
  },

  saveSchedule: async (payload: any) => {
    return await instance.post("/schedule", payload);
  },
  // §7.11 — matches new-doctor-app's scheduleApi.deleteTimeFrame.
  deleteTimeFrame: async (timeFrameId: number) => {
    return await instance.delete(`/schedule/timeframes/${timeFrameId}`);
  },
  getDailyMeeting: async (appointmentId: number) => {
    return await instance.get(`/daily/room/${appointmentId}`);
  },
};

export default DataProvider;
